// One point's forecasts: a summary of the worst band per hazard across the race
// window, then the timeline, with a tab per provider. `forecasts` is the fetch
// being shown — the latest, or an earlier one picked from the history.
import { useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, FormControlLabel, Stack, Switch, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { openInNew } from "../geo";
import {
  BAND_NONE, compass, mergeMetOffice, metricBand, pointForecasts, pointWindow, windowRecords,
  worstInRecords,
} from "./forecast";
import {
  BAND_BG, BAND_CHIP, BAND_CHIP_VARIANT, BAND_LABEL, fmtDateTime, fmtMetric, fmtPointWindow,
  fmtStepTime, fmtValue,
} from "./format";

// Table columns, in order. A column appears only if some row in view has a value.
// `band` names the metric (by field) whose thresholds colour the cell.
const COLUMNS = [
  { field: "weather", label: "Weather" },
  { field: "temp_max_c", label: "Temp °C", tip: "Maximum over the step" },
  { field: "feels_like_c", label: "Feels °C" },
  { field: "wind_mph", label: "Wind mph", dir: true },
  { field: "gust_mph", label: "Gust mph" },
  { field: "precip_mm_h", label: "Rain mm/h" },
  { field: "precip_prob", label: "Rain %" },
  { field: "heavy_rain_prob", label: "Heavy rain %" },
  { field: "lightning_prob", label: "Lightning %", tip: "Met Office three-hourly product" },
  { field: "snow_mm", label: "Snow mm" },
  { field: "visibility_m", label: "Vis m" },
  { field: "uv", label: "UV" },
  { field: "cape", label: "CAPE J/kg", tip: "Convective energy — a proxy for thunderstorm potential" },
  { field: "cloud_low_pct", label: "Low cloud %" },
  { field: "temp_850_c", label: "850 hPa °C", tip: "Free air at about 1500 m" },
  { field: "wind_850_mph", label: "850 hPa wind mph", tip: "Free air at about 1500 m" },
];

export default function PointForecast({ point, assessment, forecasts, config }) {
  const [tab, setTab] = useState("metoffice");
  const [showAll, setShowAll] = useState(false);
  const f = pointForecasts(forecasts, point.id);
  const hazards = config.hazards;
  const metricByField = useMemo(() => {
    const m = {};
    for (const h of hazards) for (const x of h.metrics) m[x.field] = x;
    return m;
  }, [hazards]);

  const mo = { hourly: f["metoffice:hourly"], three: f["metoffice:three-hourly"] };
  const windy = Object.values(f).find(x => x.provider === "windy");
  const moSeries = useMemo(() => mergeMetOffice(mo.hourly?.series, mo.three?.series),
                           [mo.hourly, mo.three]);
  const source = tab === "metoffice"
    ? { series: moSeries, errors: [mo.hourly, mo.three].filter(x => x?.error), fetched: mo.hourly || mo.three }
    : { series: windy?.series || [], errors: windy?.error ? [windy] : [], fetched: windy };
  const win = pointWindow(point, assessment);
  const inWin = windowRecords(source.series, win.start, win.end);
  const rows = showAll ? source.series : inWin;
  const columns = COLUMNS.filter(c => rows.some(r => r[c.field] !== undefined && r[c.field] !== null));
  const worst = worstInRecords(inWin, hazards);
  const hit = hazards.filter(h => worst[h.key].band !== BAND_NONE);
  const lastTime = source.series.length ? source.series[source.series.length - 1].time : null;
  const nothingFetched = !mo.hourly && !mo.three && !windy;

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
             sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{point.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {point.latitude.toFixed(4)}, {point.longitude.toFixed(4)}
            {point.elevation_m != null && ` · ${Math.round(point.elevation_m)} m`}
          </Typography>
          {win.own && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Judged over {fmtPointWindow(win.start, win.end)} — leaders arrive to back of the field.
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => openInNew(point.links.metoffice)}>
            Met Office
          </Button>
          <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => openInNew(point.links.windy)}>
            Windy
          </Button>
        </Stack>
      </Stack>

      {nothingFetched ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Not fetched yet.</Typography>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 1 }}>
            <Tab value="metoffice" label="Met Office" />
            <Tab value="windy" label={windy?.is_trial ? "Windy (trial data)" : "Windy (comparison)"} />
          </Tabs>

          {tab === "metoffice" && <MetOfficeNote forecast={mo.hourly || mo.three} />}
          {tab === "windy" && <WindyNote forecast={windy} />}
          {source.errors.map(e => (
            <Alert key={e.id} severity="error" sx={{ mt: 1 }}>
              {e.product ? `${e.product}: ` : ""}{e.error}
            </Alert>
          ))}

          {source.series.length > 0 && (
            <>
              <Box sx={{ mt: 1.5 }}>
                {inWin.length === 0 ? (
                  <Alert severity="info">
                    {win.own ? "The hours people are at this point are" : "The race window is"} outside this forecast's range
                    {lastTime ? ` (it runs to ${fmtDateTime(lastTime)})` : ""}. Fetch again nearer the day.
                  </Alert>
                ) : hit.length === 0 ? (
                  <Chip color="success" variant="outlined" label="Nothing near the thresholds during the window" />
                ) : (
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                    {hit.map(h => {
                      const w = worst[h.key];
                      return (
                        <Chip key={h.key} color={BAND_CHIP[w.band]} variant={BAND_CHIP_VARIANT[w.band] || "filled"}
                              label={`${h.label} — ${BAND_LABEL[w.band]}: ${w.metric.label.toLowerCase()} ${fmtMetric(w.metric, w.value)} (${fmtStepTime(w.time)})`} />
                      );
                    })}
                  </Stack>
                )}
              </Box>

              <FormControlLabel sx={{ mt: 1 }}
                control={<Switch size="small" checked={showAll} onChange={e => setShowAll(e.target.checked)} />}
                label={<Typography variant="body2">Show the whole forecast, not just the window</Typography>} />

              {rows.length > 0 && (
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        {columns.map(c => (
                          <TableCell key={c.field} align={c.field === "weather" ? "left" : "right"}>
                            {c.tip ? <Tooltip title={c.tip}><span>{c.label}</span></Tooltip> : c.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(r => {
                        const inside = inWin.includes(r);
                        return (
                          <TableRow key={r.time} sx={{ opacity: showAll && !inside ? 0.55 : 1 }}>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {fmtStepTime(r.time)}
                              {r.step_hours === 3 && <Typography component="span" variant="caption" color="text.secondary"> 3h</Typography>}
                            </TableCell>
                            {columns.map(c => {
                              const metric = metricByField[c.field];
                              const band = metric ? metricBand(metric, r[c.field]) : BAND_NONE;
                              return (
                                <TableCell key={c.field} align={c.field === "weather" ? "left" : "right"}
                                           title={band !== BAND_NONE ? `${BAND_LABEL[band]}: ${metric.label}` : undefined}
                                           sx={{ whiteSpace: "nowrap", bgcolor: BAND_BG[band],
                                                 fontWeight: BAND_BG[band] ? 700 : undefined }}>
                                  {fmtValue(c.field, r[c.field])}
                                  {c.dir && r.wind_dir_deg != null && (
                                    <Typography component="span" variant="caption" color="text.secondary"> {compass(r.wind_dir_deg)}</Typography>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          {source.fetched && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Fetched {fmtDateTime(source.fetched.fetched_at)}
              {source.fetched.model_run ? ` · model run ${fmtDateTime(source.fetched.model_run)}` : ""}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function MetOfficeNote({ forecast }) {
  const m = forecast?.meta;
  if (!m) return null;
  const site = m.site_name ? `Nearest Met Office site: ${m.site_name}${m.site_distance_m != null ? ` (${Math.round(m.site_distance_m)} m away)` : ""}. ` : "";
  const elev = m.model_elevation_m == null ? ""
    : m.lapse_shift_c != null
      ? `Model ground height ${m.model_elevation_m} m; temperatures adjusted ${m.lapse_shift_c > 0 ? "+" : ""}${m.lapse_shift_c} °C to ${m.point_elevation_m} m.`
      : `Model ground height ${m.model_elevation_m} m; set the point's elevation to correct temperatures to it.`;
  return <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{site}{elev}</Typography>;
}

function WindyNote({ forecast }) {
  if (!forecast) return <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Not fetched.</Typography>;
  return (
    <>
      {forecast.is_trial && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Trial API key: Windy deliberately shuffles and alters this data. It's here to prototype the
          comparison — do not base decisions on it. Use the Windy button above for the real forecast.
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Secondary comparison — {forecast.meta?.model?.toUpperCase() || "global"} model. Surface values
        are at the model's smoothed terrain, not the point's height; the 850 hPa columns show the free
        air at about 1500 m.
      </Typography>
    </>
  );
}
